import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Layers, FileText, Receipt, Scissors, Check, Loader2, ZoomIn, ZoomOut, CopyX } from "lucide-react";

const fmtChf = (n, cur = "CHF") => (n == null ? "—" : `${cur} ${Number(n).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtDate = (s) => (s ? `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(0, 4)}` : "—");
const KIND = { bill: "Eingangsrechnung", receipt: "Beleg/Quittung", invoice: "Rechnung" };

function Thumb({ companyId, itemId, attachmentId, alt, size = 320, className = "h-44 w-full rounded border border-slate-200 bg-white object-contain", testId = "relation-review-thumb" }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let revoke = null;
    setUrl(null); setFailed(false);
    apiClient.get(`/companies/${companyId}/inbox/${itemId}/thumbnail`, { params: { size, attachment_id: attachmentId || undefined }, responseType: "blob" })
      .then((r) => { revoke = URL.createObjectURL(r.data); setUrl(revoke); })
      .catch(() => setFailed(true));
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [companyId, itemId, attachmentId, size]);
  if (failed) return <div className="flex h-44 items-center justify-center rounded border border-dashed border-slate-200 bg-slate-50 text-xs text-slate-400">keine Vorschau</div>;
  if (!url) return <div className="h-44 animate-pulse rounded border border-slate-100 bg-slate-100" />;
  return <img src={url} alt={alt} className={className} data-testid={testId} />;
}

/** Lupe: alle Dokumente der Gruppe gross nebeneinander vergleichen. */
const normNr = (s) => String(s || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
/** Alle Blätter mit identischer Rechnungsnummer, Datum und Betrag → mit hoher Wahrscheinlichkeit ein Doppel-Upload. */
export const isLikelyDuplicate = (docs) => {
  if (!docs || docs.length < 2) return false;
  const [a, ...rest] = docs;
  if (!a.invoice_number || !a.invoice_date || a.total_amount == null) return false;
  return rest.every((d) => normNr(d.invoice_number) === normNr(a.invoice_number) && d.invoice_date === a.invoice_date && Math.abs(Number(d.total_amount) - Number(a.total_amount)) < 0.005);
};

function CompareOverlay({ companyId, group, open, onClose, onDecide, busy, mainId, onPickMain, order, onMove }) {
  const [fit, setFit] = useState(true);
  if (!group) return null;
  const docs = order ? order.map((id) => group.docs.find((d) => d.id === id)).filter(Boolean) : group.docs;
  const dup = isLikelyDuplicate(group.docs);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[96vw] w-[96vw] h-[94vh] flex flex-col p-4" data-testid="relation-compare-overlay">
        <DialogHeader className="flex-row items-center gap-3 space-y-0">
          <DialogTitle className="flex items-center gap-2"><ZoomIn className="h-5 w-5 text-indigo-600" /> Dokumente gross vergleichen</DialogTitle>
          <DialogDescription className="text-xs">Reihenfolge hier festlegen: «Seite 1» = Hauptdokument (wird verbucht), weitere Blätter werden in dieser Reihenfolge angehängt.</DialogDescription>
          <Button variant="outline" size="sm" className="ml-auto" onClick={() => setFit(!fit)} data-testid="relation-compare-fit-toggle">
            {fit ? <><ZoomIn className="h-4 w-4 mr-1" /> Originalgrösse</> : <><ZoomOut className="h-4 w-4 mr-1" /> Einpassen</>}
          </Button>
        </DialogHeader>
        <div className={`grid flex-1 min-h-0 gap-3 ${docs.length >= 3 ? "grid-cols-3" : "grid-cols-2"}`}>
          {docs.map((d, i) => {
            const isMain = (mainId || group.main_id) === d.id;
            return (
            <div key={d.id} className={`flex min-h-0 flex-col rounded-md border bg-slate-50 ${isMain ? "border-indigo-400 ring-2 ring-indigo-200" : "border-slate-200"}`} data-testid={`relation-compare-doc-${d.id}`}>
              <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-3 py-1.5 text-sm">
                <button type="button" onClick={() => onPickMain?.(d.id)} disabled={busy}
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium transition-colors ${isMain ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-300 bg-white text-slate-600 hover:border-indigo-400 hover:text-indigo-700"}`}
                  title={isMain ? "Dieses Blatt ist Seite 1 (Hauptdokument)" : "Dieses Blatt zu Seite 1 machen"} data-testid={`relation-compare-pick-main-${d.id}`}>
                  {isMain ? <Check className="h-3 w-3" /> : null} {isMain ? "Seite 1 · Hauptdokument" : `Seite ${i + 1} → als Seite 1`}
                </button>
                {onMove && docs.length > 2 && (
                  <span className="inline-flex gap-0.5">
                    <button type="button" disabled={i === 0 || busy} onClick={() => onMove(d.id, -1)} className="rounded border border-slate-200 px-1 text-xs disabled:opacity-30" title="nach links" data-testid={`relation-compare-move-left-${d.id}`}>◀</button>
                    <button type="button" disabled={i === docs.length - 1 || busy} onClick={() => onMove(d.id, 1)} className="rounded border border-slate-200 px-1 text-xs disabled:opacity-30" title="nach rechts" data-testid={`relation-compare-move-right-${d.id}`}>▶</button>
                  </span>
                )}
                <span className="font-medium text-slate-900 truncate">{d.file_name}{d.attachment ? " (Beilage)" : ""}</span>
                <span className="text-xs text-slate-500">{fmtDate(d.invoice_date)} · {fmtChf(d.total_amount, d.currency)}{d.invoice_number ? ` · Nr. ${d.invoice_number}` : ""}</span>
              </div>
              <div className="min-h-0 flex-1 overflow-auto p-2">
                <Thumb companyId={companyId} itemId={d.attachment ? group.main_id : d.id} attachmentId={d.attachment ? d.id : null} alt={d.file_name} size={1600}
                  className={fit ? "mx-auto h-full w-full rounded bg-white object-contain" : "mx-auto w-auto max-w-none rounded bg-white"} testId={`relation-compare-img-${d.id}`} />
              </div>
            </div>
          ); })}
        </div>
        {onDecide && (
          <DialogFooter className="gap-2 pt-3 sm:justify-end" data-testid="relation-compare-actions">
            {dup && <span className="self-center text-xs text-amber-800 mr-auto" data-testid="relation-compare-dup-hint">Rechnungsnummer, Datum und Betrag identisch → vermutlich Doppel-Upload</span>}
            <Button variant={dup ? "default" : "outline"} disabled={busy} onClick={() => onDecide("duplicate")} className={dup ? "bg-amber-500 hover:bg-amber-600 text-white" : "border-amber-300 text-amber-900 hover:bg-amber-50"} title="Identische Blätter (gleiche Nr., Datum, Betrag) – nur Seite 1 behalten, Rest in den Papierkorb" data-testid="relation-compare-duplicate">
              <CopyX className="h-4 w-4 mr-1" /> Duplikat – nur Seite 1 behalten{dup ? " · empfohlen" : ""}
            </Button>
            <Button variant="outline" disabled={busy} onClick={() => onDecide("separate")} data-testid="relation-compare-separate">
              <Scissors className="h-4 w-4 mr-1" /> Nein, getrennt lassen
            </Button>
            <Button disabled={busy} onClick={() => onDecide("merge")} variant={dup ? "outline" : "default"} className={dup ? "" : "bg-indigo-600 hover:bg-indigo-700"} data-testid="relation-compare-merge">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />} Ja, gehören zusammen
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Pflicht-Dialog: zusammengehörige Dokumente (gleicher Betrag/Datum/Seite 1/2 …) bestätigen oder trennen, bevor im Posteingang gearbeitet wird. */
export function RelationReviewDialog({ companyId, refreshKey, onResolved, onPreview }) {
  const [groups, setGroups] = useState([]);
  const [idx, setIdx] = useState(0);
  const [mainId, setMainId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [compare, setCompare] = useState(false);
  // «Später entscheiden» gilt nur für den aktuellen Besuch – beim nächsten Öffnen des Posteingangs kommt die Frage wieder zuerst
  const [snoozed, setSnoozed] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      const r = await apiClient.get(`/companies/${companyId}/inbox/relation-groups`);
      const next = r.data.groups || [];
      setGroups((prev) => {
        if (next.length > prev.length) setSnoozed(false);  // neue Gruppen (z.B. nach Upload) → wieder zeigen
        const same = prev.length === next.length && prev.every((p, i) => p.docs.map((d) => d.id).sort().join("|") === next[i].docs.map((d) => d.id).sort().join("|"));
        if (same) return prev;  // Hintergrund-Refresh ohne Änderung: Objekte behalten → Auswahl bleibt stehen
        setIdx(0);
        return next;
      });
    } catch { setGroups([]); }
  }, [companyId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const g = groups[idx];
  const dup = isLikelyDuplicate(g?.docs);
  const [order, setOrder] = useState([]);
  // Nur bei einer *anderen* Gruppe zurücksetzen – beim Hintergrund-Refresh bleibt die Wahl des Nutzers (Seite 1 / Reihenfolge) erhalten
  const groupKey = g ? g.docs.map((d) => d.id).sort().join("|") : "";
  useEffect(() => {
    if (!g) { setMainId(null); setOrder([]); return; }
    setMainId((cur) => (cur && g.docs.some((d) => d.id === cur) ? cur : g.main_id || null));
    setOrder((cur) => (cur.length === g.docs.length && cur.every((id) => g.docs.some((d) => d.id === id)) ? cur : [g.main_id, ...g.docs.map((d) => d.id).filter((id) => id !== g.main_id)]));
  }, [groupKey]); // eslint-disable-line

  // Seite 1 wählen = an den Anfang der Reihenfolge; Wahl sofort am Dokument speichern (überlebt Neuladen)
  const savePref = (main, ord) => {
    if (!g || !main) return;
    apiClient.post(`/companies/${companyId}/inbox/relation-groups/preference`, { item_ids: g.docs.map((d) => d.id), main_id: main, order: ord }).catch(() => {});
  };
  const pickMain = (id) => {
    setMainId(id);
    setOrder((o) => { const next = [id, ...o.filter((x) => x !== id)]; savePref(id, next); return next; });
  };
  const moveDoc = (id, dir) => {
    setOrder((o) => {
      const i = o.indexOf(id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= o.length) return o;
      const n = [...o];
      [n[i], n[j]] = [n[j], n[i]];
      setMainId(n[0]);
      return n;
    });
  };

  const notifyRematch = (rematched) => {
    const hits = (rematched || []).filter((r) => r.matches?.length);
    if (!hits.length) return false;
    hits.forEach((r) => toast.info(`Für «${r.file_name}» wurde ein anderer passender Beleg gefunden: ${r.matches.map((m) => m.file_name).join(", ")} – bitte prüfen`, { duration: 8000 }));
    return true;
  };

  const act = async (action) => {
    if (!g) return;
    setBusy(true);
    try {
      let rematched = [];
      if (action === "duplicate") {
        const chosenMain = mainId || g.main_id;
        const ids = g.docs.map((d) => d.id);
        const r = await apiClient.post(`/companies/${companyId}/inbox/relation-groups/resolve`, { item_ids: ids, action: "duplicate", main_id: chosenMain });
        const n = r.data?.removed?.length || 0;
        toast.success(`Duplikat erkannt – ${n} identische${n === 1 ? "s Blatt" : " Blätter"} entfernt, nur Seite 1 bleibt (Papierkorb).`);
      } else if (g.type === "auto_merged" && action === "separate") {
        for (const d of g.docs.filter((x) => x.attachment)) {
          const r = await apiClient.post(`/companies/${companyId}/inbox/${g.main_id}/attachments/${d.id}/detach`);
          rematched = rematched.concat(r.data?.rematched || []);
        }
        toast.success("Dokumente getrennt – liegen wieder einzeln im Posteingang");
      } else {
        const ids = order.length ? order.filter((id) => g.docs.some((d) => d.id === id && !d.attachment)) : g.docs.filter((x) => !x.attachment).map((x) => x.id);
        const a = g.type === "auto_merged" ? "confirm" : action;
        let chosenMain = mainId || g.main_id;
        if (a === "confirm" && chosenMain !== g.main_id) {
          // andere Seite als Seite 1 gewählt → Beilage zum Hauptdokument machen
          await apiClient.post(`/companies/${companyId}/inbox/${g.main_id}/attachments/${chosenMain}/promote`);
          chosenMain = g.main_id;
        }
        const r = await apiClient.post(`/companies/${companyId}/inbox/relation-groups/resolve`, { item_ids: ids, action: a, main_id: chosenMain });
        rematched = r.data?.rematched || [];
        toast.success(a === "separate" ? "Als getrennte Dokumente bestätigt" : "Zusammengehörigkeit bestätigt – als ein Dokument mit Beilage");
      }
      if (action === "separate" && notifyRematch(rematched)) {
        await load();  // neue Gruppen mit den anderen passenden Belegen sofort anzeigen
      } else {
        const rest = groups.filter((_, i) => i !== idx);
        setGroups(rest);
        setIdx(0);
      }
      onResolved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const snooze = () => setSnoozed(true);
  const open = !!g && !snoozed;

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="max-w-3xl [&>button]:hidden" data-testid="relation-review-dialog" onEscapeKeyDown={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-indigo-600" />
            Gehören diese Dokumente zusammen?
            <span className="ml-auto text-xs font-normal text-slate-500" data-testid="relation-review-counter">{idx + 1} von {groups.length}</span>
          </DialogTitle>
          <DialogDescription>
            {g?.type === "auto_merged"
              ? "Diese Dokumente wurden automatisch zusammengeführt. Bitte bestätigen, bevor du im Posteingang weiterarbeitest."
              : "Das System hat Hinweise gefunden, dass diese Dokumente zusammengehören (z.B. Rechnung + Zahlungsbeleg, Seite 1 + 2). Bitte entscheiden, bevor du im Posteingang weiterarbeitest."}
          </DialogDescription>
        </DialogHeader>
        {g && (
          <>
            <div className="flex flex-wrap items-center gap-1.5" data-testid="relation-review-reasons">
              {dup && <span className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-900 inline-flex items-center gap-1" data-testid="relation-review-dup-hint"><CopyX className="h-3 w-3" />Vermutlich Doppel-Upload: Nr., Datum und Betrag identisch</span>}
              {g.reasons.map((r) => <span key={r} className="rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-xs text-indigo-800">{r}</span>)}
              <Button variant="outline" size="sm" className="ml-auto h-7" onClick={() => setCompare(true)} title="Beide Dokumente gross nebeneinander anzeigen" data-testid="relation-review-zoom-btn">
                <ZoomIn className="h-4 w-4 mr-1" /> Gross vergleichen
              </Button>
            </div>
            <div className={`grid gap-3 ${g.docs.length >= 3 ? "grid-cols-3" : "grid-cols-2"}`} data-testid="relation-review-docs">
              {g.docs.map((d) => {
                const Icon = d.kind === "bill" ? FileText : Receipt;
                const selectable = true;
                return (
                  <div key={d.id} className={`rounded-md border p-2 text-sm ${mainId === d.id ? "border-indigo-400 bg-indigo-50/60" : "border-slate-200"}`} data-testid={`relation-review-doc-${d.id}`}>
                    <button type="button" className="group relative w-full" onClick={() => setCompare(true)} title="Gross vergleichen" data-testid={`relation-review-preview-${d.id}`}>
                      <Thumb companyId={companyId} itemId={d.attachment ? g.main_id : d.id} attachmentId={d.attachment ? d.id : null} alt={d.file_name} />
                      <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-white/95 px-2 py-1 text-xs font-medium text-indigo-700 shadow transition-colors group-hover:bg-indigo-600 group-hover:text-white"><ZoomIn className="h-4 w-4" /> Lupe</span>
                    </button>
                    <div className="mt-2 flex items-start gap-2">
                      {selectable && (
                        <input type="radio" name="relmain" className="mt-1" checked={mainId === d.id} onChange={() => pickMain(d.id)} title="Seite 1 – Hauptdokument (wird verbucht)" data-testid={`relation-review-main-${d.id}`} />
                      )}
                      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-slate-900" title={d.file_name}>{d.file_name}{d.attachment ? " (Beilage)" : ""}</p>
                        <p className="text-xs text-slate-500">{KIND[d.kind] || d.kind || "Dokument"}{d.supplier_name ? ` · ${d.supplier_name}` : ""}</p>
                        <p className="text-xs text-slate-600">{fmtDate(d.invoice_date)} · <span className="font-medium tabular-nums">{fmtChf(d.total_amount, d.currency)}</span>{d.invoice_number ? ` · Nr. ${d.invoice_number}` : ""}</p>
                      </div>

                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-slate-500">{g.type === "auto_merged" ? "Das markierte Dokument ist Seite 1 (Hauptdokument, wird verbucht) – zum Tauschen einfach das andere markieren." : "Das markierte Dokument wird zum Hauptdokument (wird verbucht), die anderen werden als Beilagen angehängt."}</p>
          </>
        )}
        <DialogFooter className="gap-2 sm:justify-between flex-wrap">
          <button type="button" onClick={snooze} className="text-xs text-slate-400 hover:text-slate-700 underline whitespace-nowrap" data-testid="relation-review-snooze">Später entscheiden</button>
          <div className="flex gap-2 flex-wrap justify-end">
            <Button variant={dup ? "default" : "outline"} disabled={busy} onClick={() => act("duplicate")} className={dup ? "bg-amber-500 hover:bg-amber-600 text-white" : "border-amber-300 text-amber-900 hover:bg-amber-50"} title="Identische Blätter (gleiche Nr., Datum, Betrag) – nur Seite 1 behalten, Rest in den Papierkorb" data-testid="relation-review-duplicate">
              <CopyX className="h-4 w-4 mr-1" /> Duplikat{dup ? " · empfohlen" : ""}
            </Button>
            <Button variant="outline" disabled={busy} onClick={() => act("separate")} data-testid="relation-review-separate">
              <Scissors className="h-4 w-4 mr-1" /> Nein, getrennt lassen
            </Button>
            <Button disabled={busy} onClick={() => act("merge")} variant={dup ? "outline" : "default"} className={dup ? "" : "bg-indigo-600 hover:bg-indigo-700"} data-testid="relation-review-merge">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />} Ja, gehören zusammen
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
      <CompareOverlay companyId={companyId} group={g} open={compare && !!g} onClose={() => setCompare(false)} busy={busy}
        mainId={mainId} onPickMain={pickMain} order={order} onMove={g?.type === "auto_merged" ? null : moveDoc}
        onDecide={async (a) => { await act(a); setCompare(false); }} />
    </Dialog>
  );
}
